import React, { useEffect, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { PasswordInput } from './PasswordInput';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (password: string) => void;
  ssid: string;
};

// The minimum length for a standard WPA/WPA2/WPA3 Pre-Shared Key (PSK/Personal) is 8 characters.
const MIN_WIFI_PASSWORD_LENGTH = 8;

export function WiFiPasswordDialog({ isOpen, onClose, onConfirm, ssid }: Props) {
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const buttonBorderColor = useColorModeValue('customGrey.400', 'customGrey.600');
  const backgroundColor = useColorModeValue('customGrey.100', 'customGrey.800');
  const { t } = useTranslation();
  const [passwordInput, setPasswordInput] = useState('');

  const isTooShort = passwordInput.length < MIN_WIFI_PASSWORD_LENGTH;
  // Only surface the error once the user has started typing, to avoid flagging the empty initial state.
  const showLengthError = passwordInput.length > 0 && isTooShort;

  // Clear password input when dialog closes to prevent pre-filling incorrect passwords on next attempt
  useEffect(() => {
    if (!isOpen) {
      setPasswordInput('');
    }
  }, [isOpen]);

  function onClickConfirm() {
    if (isTooShort) return;
    onConfirm(passwordInput);
    onClose();
  }

  async function onChangePassword(password: string) {
    setPasswordInput(password);
  }

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef as React.RefObject<HTMLButtonElement>}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent top={'200px'} bgColor={backgroundColor} mx={8} rounded={16} mt={0} overflowX="hidden">
          <AlertDialogBody my={4} textAlign="center" data-testid="wifi-password-dialog">
            {t('Set Up Hardware.enter wifi pw for ssid', { ssid })}
            <FormControl mt={3} isInvalid={showLengthError}>
              <PasswordInput
                onChange={onChangePassword}
                value={passwordInput}
                backgroundColor={backgroundColor}
                borderColor={'#9FA0A4'}
              />
              <FormErrorMessage data-testid="wifi-password-error" textAlign="left">
                {t('Set Up Hardware.wifi password min length', { minLength: MIN_WIFI_PASSWORD_LENGTH })}
              </FormErrorMessage>
            </FormControl>
          </AlertDialogBody>

          <Flex w="100%" justifyContent={'space-around'}>
            <Button
              w="100%"
              rounded={0}
              py={6}
              fontWeight="normal"
              borderRight="1px solid"
              borderTop="1px solid"
              borderColor={buttonBorderColor}
              variant="ghost"
              onClick={onClose}
              ref={cancelRef}
              data-testid="cancel-button"
            >
              {t('Common.cancel')}
            </Button>

            <Button
              color="#3DCD58"
              w="100%"
              rounded={0}
              py={6}
              variant="ghost"
              borderTop="1px solid"
              borderColor={buttonBorderColor}
              onClick={onClickConfirm}
              isDisabled={isTooShort}
              data-testid="confirm-button"
            >
              OK
            </Button>
          </Flex>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
